/**
 * Registre unique : capacités (permission_code), profils par rôle, routes et menu.
 * Menu v3 : permissions nav.* 1:1 avec les entrées feuilles du sidebar.
 */
import {
  OPERATOR_FAMILY_ROLE_TYPES,
  PRODUCTION_MANAGER_ROLE_TYPES,
  WORKSHOP_ATELIER_ROLE_TYPES,
} from './operatorRoles';
import {
  ADMIN_ROUTE_NAV_PERMISSION_CODES,
  MENU_LEAF_PERMISSION_CODES,
  MENU_MATRIX_DISPLAY_ROWS,
  MENU_SIDEBAR_LEAF_CONFIG,
  NAV_LEGACY_READ_ONLY_CODES,
  NAV_PERMISSION_LEGACY_ALIASES,
  codesIncludingNavAliases,
  navPermissions,
} from './menuPermissionSpec';

export {
  MENU_LEAF_PERMISSION_CODES,
  MENU_MATRIX_DISPLAY_ROWS,
  NAV_PERMISSION_LEGACY_ALIASES,
  NAV_LEGACY_READ_ONLY_CODES,
  codesIncludingNavAliases,
  navPermissions,
} from './menuPermissionSpec';

/** @deprecated Conservé pour références legacy hors menu */
export const ADMIN_PLATFORM_PERMISSION = 'admin.platform';

/** Codes masqués dans la matrice (anciens profils, non utilisés pour le menu). */
export const LEGACY_NON_CONFIGURABLE_PERMISSION_CODES = [
  ...NAV_LEGACY_READ_ONLY_CODES,
  'billing.create',
  'billing.edit',
  'billing.approve',
  'billing.discount',
  'market.view',
  'market.manage',
  'analytics.view',
  'analytics.export',
  'user.create',
  'user.edit',
  'user.delete',
];

export const PERMISSION_ROUTE_CODES = {
  home: navPermissions('nav.dashboard'),
  patients: navPermissions('nav.patients'),
  cases: navPermissions('nav.cases'),
  training: navPermissions('nav.training'),
  dentistBillingPack: navPermissions('nav.dentist_billing_pack'),
  packUser: ['nav.pack_user'],
  productionTracking: navPermissions('nav.production.tracking'),
  productionWorkshop: navPermissions('nav.production.workshop'),
  adminUi: [...ADMIN_ROUTE_NAV_PERMISSION_CODES],
};

/** @deprecated Utiliser PERMISSION_ROUTE_CODES */
export const P = PERMISSION_ROUTE_CODES;

/** Matrice admin : uniquement la visibilité des pages du menu (nav.*). */
export const PERMISSION_GROUPS = [
  {
    id: 'menu',
    labelFr: 'Menu (pages visibles)',
    labelEn: 'Menu (visible pages)',
    codes: MENU_LEAF_PERMISSION_CODES,
    configurable: true,
    useMatrixDisplayRows: true,
  },
];

const MENU_DENTIST = [
  'nav.dashboard',
  'nav.patients',
  'nav.cases',
  'nav.dentist_billing_pack',
  'nav.training',
];
const MENU_OPERATOR_BASE = ['nav.dashboard', 'nav.cases'];
const MENU_PROD_MANAGER = [
  ...MENU_OPERATOR_BASE,
  'nav.production.tracking',
  'nav.production.workshop',
];
/**
 * Profils recommandés : entrées menu uniquement (actions métier = rôle utilisateur).
 */
export const ROLE_CAPABILITY_PRESETS = {
  administrator: null,

  dentist_client: [...MENU_DENTIST],

  dentist_supplier: ['nav.dashboard', 'nav.cases'],

  distributor: ['nav.cases'],

  production_supplier: ['nav.cases'],

  production_responsible: [...MENU_PROD_MANAGER, 'nav.admin.machines'],

  logistics_coordinator: [...MENU_PROD_MANAGER, 'nav.admin.machines'],

  agent_administratif: ['nav.dashboard', 'nav.patients', 'nav.cases'],

  operator_3d: [
    ...MENU_OPERATOR_BASE,
    'nav.production.tracking',
    'nav.production.workshop',
  ],

  operator_thermo: [
    ...MENU_OPERATOR_BASE,
    'nav.production.tracking',
    'nav.production.workshop',
  ],

  operator_finition: [
    ...MENU_OPERATOR_BASE,
    'nav.production.tracking',
    'nav.production.workshop',
  ],

  operator_qualite: [
    ...MENU_OPERATOR_BASE,
    'nav.production.tracking',
    'nav.production.workshop',
  ],

  operators: [
    ...MENU_OPERATOR_BASE,
    'nav.production.tracking',
    'nav.production.workshop',
  ],

  livraison: [
    ...MENU_OPERATOR_BASE,
    'nav.production.tracking',
    'nav.production.workshop',
  ],

  coursier: ['nav.dashboard'],
};

export const ROUTE_ACCESS = {
  sharedHome: {
    allowedRoles: [
      'dentist_client',
      'administrator',
      'dentist_supplier',
      'agent_administratif',
      ...PRODUCTION_MANAGER_ROLE_TYPES,
      ...WORKSHOP_ATELIER_ROLE_TYPES,
    ],
    allowedPermissions: PERMISSION_ROUTE_CODES.home,
  },
  training: {
    allowedRoles: ['administrator', 'dentist_client'],
    allowedPermissions: PERMISSION_ROUTE_CODES.training,
  },
  packUser: {
    allowedRoles: ['dentist_client', 'administrator'],
    allowedPermissions: PERMISSION_ROUTE_CODES.packUser,
  },
  dentistHub: {
    allowedRoles: ['dentist_client', 'administrator'],
    allowedPermissions: [
      ...PERMISSION_ROUTE_CODES.home,
      ...PERMISSION_ROUTE_CODES.dentistBillingPack,
    ],
  },
  admin: {
    allowedRoles: ['administrator'],
    allowedPermissions: PERMISSION_ROUTE_CODES.adminUi,
  },
  patients: {
    allowedRoles: ['administrator', 'dentist_client', 'agent_administratif'],
    allowedPermissions: PERMISSION_ROUTE_CODES.patients,
  },
  patientProfile: {
    allowedRoles: ['dentist_client', 'administrator', 'agent_administratif'],
    allowedPermissions: PERMISSION_ROUTE_CODES.patients,
  },
  cases: {
    allowedRoles: [
      'dentist_client',
      'administrator',
      'dentist_supplier',
      'agent_administratif',
      ...OPERATOR_FAMILY_ROLE_TYPES,
    ],
    allowedPermissions: PERMISSION_ROUTE_CODES.cases,
  },
  productionDesk: {
    allowedRoles: [
      'administrator',
      ...PRODUCTION_MANAGER_ROLE_TYPES,
      ...WORKSHOP_ATELIER_ROLE_TYPES,
    ],
    allowedPermissions: PERMISSION_ROUTE_CODES.productionTracking,
  },
  workshop: {
    allowedRoles: [
      'administrator',
      ...PRODUCTION_MANAGER_ROLE_TYPES,
      ...WORKSHOP_ATELIER_ROLE_TYPES,
    ],
    allowedPermissions: [
      ...PERMISSION_ROUTE_CODES.productionWorkshop,
      ...PERMISSION_ROUTE_CODES.productionTracking,
    ],
  },
  /** Fiche de production (QR + patient / cas) — ouverte depuis dossier ou atelier */
  productionSheet: {
    allowedRoles: [
      'administrator',
      'agent_administratif',
      ...OPERATOR_FAMILY_ROLE_TYPES,
    ],
    allowedPermissions: [
      ...PERMISSION_ROUTE_CODES.cases,
      ...PERMISSION_ROUTE_CODES.productionTracking,
      ...PERMISSION_ROUTE_CODES.productionWorkshop,
    ],
  },
  clientDelivery: {
    allowedRoles: ['client', 'administrator'],
    allowedPermissions: [],
  },
};

function sidebarLeafItem(code, { t, label, icon, path, roles, permissionsAny }) {
  return {
    label,
    path,
    icon,
    className: 'used',
    roles,
    permissionsAny: permissionsAny ?? navPermissions(code),
  };
}

function permissionsForLeaf(code) {
  return navPermissions(code);
}

/**
 * Menu principal v3 — aligné matrice permissions (nav.* par feuille).
 * @param {{ t: Function, isDentistOnlyClient: boolean }} ctx
 */
export function buildSidebarNavigationItems({ t, isDentistOnlyClient }) {
  const productionChildren = [
    sidebarLeafItem('nav.production.tracking', {
      t,
      label: t('sidebar.nav.production.productionTracking'),
      icon: 'Activity',
      path: '/production-tracking-dashboard',
      roles: MENU_SIDEBAR_LEAF_CONFIG['nav.production.tracking'].roles,
      permissionsAny: permissionsForLeaf('nav.production.tracking'),
    }),
    sidebarLeafItem('nav.production.workshop', {
      t,
      label: t('sidebar.nav.production.workshopProduction'),
      icon: 'Wrench',
      path: '/atelier-lot',
      roles: MENU_SIDEBAR_LEAF_CONFIG['nav.production.workshop'].roles,
      permissionsAny: permissionsForLeaf('nav.production.workshop'),
    }),
  ];

  const billingChildren = [
    sidebarLeafItem('nav.billing.invoices', {
      t,
      label: t('sidebar.nav.administration.billing.generate'),
      icon: 'FileText',
      path: '/dentist-invoices-tracking',
      roles: ['administrator'],
      permissionsAny: permissionsForLeaf('nav.billing.invoices'),
    }),
    sidebarLeafItem('nav.billing.config', {
      t,
      label: t('sidebar.nav.administration.billing.configuration'),
      icon: 'Settings',
      path: '/billing-phases-management',
      roles: ['administrator'],
      permissionsAny: permissionsForLeaf('nav.billing.config'),
    }),
  ];

  const paymentChildren = [
    sidebarLeafItem('nav.payment.schedule', {
      t,
      label: t('sidebar.nav.administration.paymentSettings'),
      icon: 'Calendar',
      path: '/advanced-payment-configuration',
      roles: ['administrator'],
      permissionsAny: permissionsForLeaf('nav.payment.schedule'),
    }),
    sidebarLeafItem('nav.payment.overview', {
      t,
      label: t('sidebar.nav.administration.billing.billing'),
      icon: 'CreditCard',
      path: '/billing-overview',
      roles: ['administrator'],
      permissionsAny: permissionsForLeaf('nav.payment.overview'),
    }),
    sidebarLeafItem('nav.payment.discounts', {
      t,
      label: t('sidebar.nav.administration.billing.advancedBilling'),
      icon: 'Percent',
      path: '/advanced-billing-management',
      roles: ['administrator'],
      permissionsAny: permissionsForLeaf('nav.payment.discounts'),
    }),
  ];

  const adminChildren = [
    sidebarLeafItem('nav.admin.users', {
      t,
      label: t('sidebar.nav.administration.usersAndAccess'),
      icon: 'UserCog',
      path: '/admin-users-management',
      roles: ['administrator'],
      permissionsAny: permissionsForLeaf('nav.admin.users'),
    }),
    sidebarLeafItem('nav.admin.workflow', {
      t,
      label: t('sidebar.nav.administration.phaseTemplates'),
      icon: 'Layers',
      path: '/phase-templates',
      roles: ['administrator'],
      permissionsAny: permissionsForLeaf('nav.admin.workflow'),
    }),
    sidebarLeafItem('nav.admin.packs_services', {
      t,
      label: t('sidebar.nav.administration.packsAndServices'),
      icon: 'Package',
      path: '/admin/packs-services',
      roles: ['administrator'],
      permissionsAny: permissionsForLeaf('nav.admin.packs_services'),
    }),
    sidebarLeafItem('nav.admin.machines', {
      t,
      label: t('sidebar.nav.administration.machineFleet'),
      icon: 'Cog',
      path: '/machine-fleet-management',
      roles: ['administrator'],
      permissionsAny: permissionsForLeaf('nav.admin.machines'),
    }),
    sidebarLeafItem('nav.admin.countries', {
      t,
      label: t('sidebar.nav.administration.internationalMarkets'),
      icon: 'Globe',
      path: '/international-market-management',
      roles: ['administrator'],
      permissionsAny: permissionsForLeaf('nav.admin.countries'),
    }),
    sidebarLeafItem('nav.admin.integrations', {
      t,
      label: t('sidebar.nav.administration.integrations'),
      icon: 'Plug',
      path: '/admin/integrations',
      roles: ['administrator'],
      permissionsAny: permissionsForLeaf('nav.admin.integrations'),
    }),
    sidebarLeafItem('nav.admin.platform_messages', {
      t,
      label: t('sidebar.nav.administration.platformMessages'),
      icon: 'Megaphone',
      path: '/admin/platform-messages',
      roles: ['administrator'],
      permissionsAny: permissionsForLeaf('nav.admin.platform_messages'),
    }),
  ];

  const unionRoles = (items) => [...new Set(items.flatMap((c) => c.roles || []))];
  const unionPerms = (items) => [...new Set(items.flatMap((c) => c.permissionsAny || []))];

  return [
    sidebarLeafItem('nav.dashboard', {
      t,
      label: isDentistOnlyClient ? 'Accueil' : t('sidebar.nav.dashboard'),
      icon: isDentistOnlyClient ? 'LayoutGrid' : 'LayoutDashboard',
      path: isDentistOnlyClient ? '/dentiste' : '/dashboard',
      roles: MENU_SIDEBAR_LEAF_CONFIG['nav.dashboard'].roles,
      permissionsAny: permissionsForLeaf('nav.dashboard'),
    }),
    sidebarLeafItem('nav.patients', {
      t,
      label: t('sidebar.nav.patients'),
      icon: 'Users',
      path: '/patients-list',
      roles: MENU_SIDEBAR_LEAF_CONFIG['nav.patients'].roles,
      permissionsAny: permissionsForLeaf('nav.patients'),
    }),
    sidebarLeafItem('nav.cases', {
      t,
      label: t('sidebar.nav.conception'),
      icon: 'ClipboardList',
      path: '/cases-list',
      roles: MENU_SIDEBAR_LEAF_CONFIG['nav.cases'].roles,
      permissionsAny: permissionsForLeaf('nav.cases'),
    }),
    sidebarLeafItem('nav.dentist_billing_pack', {
      t,
      label: t('sidebar.nav.dentistBillingPack'),
      icon: 'Receipt',
      path: '/dentiste/facturation-pack',
      roles: MENU_SIDEBAR_LEAF_CONFIG['nav.dentist_billing_pack'].roles,
      permissionsAny: permissionsForLeaf('nav.dentist_billing_pack'),
    }),
    sidebarLeafItem('nav.training', {
      t,
      label: t('sidebar.nav.training'),
      icon: 'GraduationCap',
      path: '/training-videos-library',
      roles: MENU_SIDEBAR_LEAF_CONFIG['nav.training'].roles,
      permissionsAny: permissionsForLeaf('nav.training'),
    }),
    {
      label: t('sidebar.nav.production.label'),
      icon: 'Factory',
      className: 'used',
      roles: unionRoles(productionChildren),
      permissionsAny: unionPerms(productionChildren),
      children: productionChildren,
    },
    {
      label: t('sidebar.nav.billing.label'),
      icon: 'Receipt',
      className: 'used',
      roles: unionRoles(billingChildren),
      permissionsAny: unionPerms(billingChildren),
      children: billingChildren,
    },
    {
      label: t('sidebar.nav.paiement'),
      icon: 'CreditCard',
      className: 'used',
      roles: unionRoles(paymentChildren),
      permissionsAny: unionPerms(paymentChildren),
      children: paymentChildren,
    },
    {
      label: t('sidebar.nav.administration.label'),
      icon: 'Shield',
      className: 'used',
      roles: unionRoles(adminChildren),
      permissionsAny: unionPerms(adminChildren),
      children: adminChildren,
    },
  ];
}

export function getPresetPermissionCodesForRole(roleType) {
  if (!roleType) return [];
  if (roleType === 'administrator') return null;
  const preset = ROLE_CAPABILITY_PRESETS[roleType];
  return preset ? [...new Set(preset)] : [];
}

export function isPermissionConfigurable(permissionCode) {
  if (!permissionCode) return false;
  return !LEGACY_NON_CONFIGURABLE_PERMISSION_CODES.includes(permissionCode);
}

export function groupPermissionsForMatrix(permissions, language = 'FR') {
  const byCode = Object.fromEntries(
    (permissions || []).map((p) => [p?.permissionCode || p?.permission_code, p]),
  );
  const isFr = String(language).toUpperCase() === 'FR';

  return PERMISSION_GROUPS.map((group) => {
    const base = {
      ...group,
      label: isFr ? group.labelFr : group.labelEn,
    };

    if (group.useMatrixDisplayRows) {
      const displayRows = MENU_MATRIX_DISPLAY_ROWS.map((row) => {
        if (row.kind === 'section') {
          return {
            kind: 'section',
            label: isFr ? row.labelFr : row.labelEn,
          };
        }
        const perm = byCode[row.code];
        if (!perm) return null;
        return {
          kind: 'permission',
          permission: { ...perm, matrixIndent: row.indent ?? 0 },
        };
      }).filter(Boolean);
      return {
        ...base,
        permissions: displayRows
          .filter((r) => r.kind === 'permission')
          .map((r) => r.permission),
        displayRows,
      };
    }

    return {
      ...base,
      permissions: group.codes.map((code) => byCode[code]).filter(Boolean),
    };
  }).filter((g) => g.permissions?.length > 0);
}
